/* Small, self-contained checkout-acceptance UI used by both €1 flows.
 * Renders a required checkbox + short disclosure. Consumer must actively
 * check the box before the Stripe controls mount (see VoiceModal.jsx and
 * PublishPaymentScreen.jsx — both gate `<Elements>` on `accepted === true`).
 *
 * Two modes:
 *   • mode="paid"   → immediate-performance wording, refund/consumer-rights note
 *   • mode="free"   → simple Terms acceptance, no immediate-performance wording
 *
 * The label text is required verbatim by the approved product spec — do not
 * rewrite it. Any wording change requires a `TERMS_VERSION` bump on the
 * backend (see backend/routes/payments.py::TERMS_VERSION).
 */
export default function TermsAcceptance({ accepted, onChange, mode, testId }) {
  const isPaid = mode === "paid";
  return (
    <div className="mt-6" data-testid={testId || "terms-acceptance"}>
      <label className="flex items-start gap-3 cursor-pointer select-none">
        <input
          type="checkbox"
          checked={accepted}
          onChange={(e) => onChange(e.target.checked)}
          className="mt-[3px] h-4 w-4 accent-[color:var(--oh-red)] cursor-pointer"
          data-testid={`${testId || "terms"}-checkbox`}
          required
        />
        <span className="text-xs text-[color:var(--oh-ink-2)] leading-[1.55]">
          {isPaid ? (
            <>
              I agree to the{" "}
              <a href="/terms" target="_blank" rel="noopener noreferrer"
                 className="underline underline-offset-2 hover:text-[color:var(--oh-ink)]"
                 data-testid={`${testId || "terms"}-link`}>
                Terms of Service
              </a>{" "}
              and expressly request that VOIHU begins and completes the service
              immediately after successful payment. I understand that, once the
              service has been fully performed and my VOICE has been added, I
              will lose my statutory right of withdrawal, except where
              mandatory law provides otherwise.
            </>
          ) : (
            <>
              I agree to the{" "}
              <a href="/terms" target="_blank" rel="noopener noreferrer"
                 className="underline underline-offset-2 hover:text-[color:var(--oh-ink)]"
                 data-testid={`${testId || "terms"}-link`}>
                Terms of Service
              </a>
              .
            </>
          )}
        </span>
      </label>
      {isPaid && (
        <p className="mt-3 text-[11px] text-[color:var(--oh-ink-3)] leading-relaxed">
          €1 participation fee. Your VOICE is added immediately after
          successful payment. Mandatory consumer rights remain unaffected.
        </p>
      )}
      <p className="mt-2 text-[11px] text-[color:var(--oh-ink-3)] leading-relaxed">
        See our{" "}
        <a href="/privacy" target="_blank" rel="noopener noreferrer"
           className="underline underline-offset-2 hover:text-[color:var(--oh-ink)]"
           data-testid={`${testId || "terms"}-privacy-link`}>
          Privacy Policy
        </a>
        .
      </p>
    </div>
  );
}
